import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import AuthApi from '../api/AuthApi';
import { ROLES_AND_PERMISSION_ENABLED, useAuthContext } from '../context/auth';
import { UserContextProps, UserProvider } from '../context/userContext';
import {
	INVALID_AUTHORIZATION_MESSAGE,
	INVALID_AUTHORIZATION_REQUIRED_PERMISSIONS_TITLE_MESSAGE,
} from '../data/labelsAndMessages';
import { Permission } from '../permissions';
import { buildRoleTree } from '../util/adminUtil';
import { NamespaceAdminApp } from './admin/namespace';
import { RoleTree, UserRole } from './admin/namespace/types';
import Error from './Error';
import LargeCenterSpinner from './LargeCenterSpinner';
import RoleSelect from './RoleSelect';
import TenantApp from './TenantApp';

const NamespaceApp = () => {
	const authData = useAuthContext();
	const [error, setError] = useState(false);
	const [selectedUserRole, setSelectedUserRole] = useState<UserRole>();
	const [roleTree, setRoleTree] = useState<RoleTree>();

	const changeRole = () => {
		setSelectedUserRole(null);
	};

	const { data: user, isLoading: isUserLoading } = useQuery({
		queryKey: ['user'],
		queryFn: () => AuthApi.getUserInfo(),
		refetchOnWindowFocus: false,
	});

	const { isLoading: isSsoAccountInfoLoading, data: ssoAccount } = useQuery({
		queryKey: ['sso-account'],
		queryFn: () => AuthApi.getLoggedInAccount(),
		onSuccess: (account) => {
			authData.setAuth(account, account.username, account.hostName);
		},
		onError: () => {
			setError(true);
		},
		refetchOnWindowFocus: false,
	});

	const accountId = ssoAccount?.accountId;

	const { isLoading: isAccountLoading } = useQuery({
		queryKey: ['account', accountId],
		enabled: !!accountId,
		queryFn: () => AuthApi.getAccount(accountId),
		onSuccess: (account) => {
			authData.setPermissions(account.roles);
		},
		refetchOnWindowFocus: false,
	});

	const userUuid = user?.userUuid;

	const { data: userRoles, isLoading: isUserRolesLoading } = useQuery({
		enabled: !!userUuid,
		queryKey: ['assignedRoles', userUuid],
		queryFn: () => AuthApi.getUserRoles(userUuid),
		onSuccess: (data) => {
			setRoleTree(buildRoleTree(data));
			setSelectedUserRole(data.find((role) => role.role === user.activeRole));
		},
		refetchOnWindowFocus: false,
	});

	const { mutateAsync: activeRole } = useMutation({
		mutationFn: (userRole: UserRole) => {
			return AuthApi.activateUserRole(
				user.userUuid,
				userRole.role,
				userRole.accountType,
			);
		},
		onSuccess: (_data, userRole) => {
			setSelectedUserRole(userRole);
			authData.logout();
		},
	});

	const isLoading =
		isUserLoading ||
		isSsoAccountInfoLoading ||
		(isUserRolesLoading && !!userUuid) ||
		(isAccountLoading && !!accountId);

	if (isLoading) {
		return <LargeCenterSpinner />;
	}

	if (!selectedUserRole) {
		return <RoleSelect activateRole={activeRole} roleTree={roleTree} />;
	}

	const userValues: UserContextProps = {
		user,
		userRoles,
		selectedUserRole,
		activeRole: user.activeRole,
		setSelectedUserRole,
		changeRole,
	};

	if (selectedUserRole?.namespace) {
		return (
			<UserProvider value={userValues}>
				<BrowserRouter>
					<NamespaceAdminApp error={error} />
				</BrowserRouter>
			</UserProvider>
		);
	}

	const rolesAndPermissionEnabled =
		authData.auth?.configProperties[ROLES_AND_PERMISSION_ENABLED];

	const missingPermissions: Permission[] = [];

	!authData.hasPermission('product.fetch', rolesAndPermissionEnabled) &&
		missingPermissions.push('product.fetch');
	!authData.hasPermission('ontology.fetch', rolesAndPermissionEnabled) &&
		missingPermissions.push('ontology.fetch');
	!authData.hasPermission('workflow.fetch', rolesAndPermissionEnabled) &&
		missingPermissions.push('workflow.fetch');
	!authData.hasPermission('tenant.fetch', rolesAndPermissionEnabled) &&
		missingPermissions.push('tenant.fetch');

	if (rolesAndPermissionEnabled && missingPermissions.length > 0) {
		return (
			<Error
				error={
					INVALID_AUTHORIZATION_MESSAGE() +
					' ' +
					INVALID_AUTHORIZATION_REQUIRED_PERMISSIONS_TITLE_MESSAGE() +
					missingPermissions.join(', ')
				}
				primaryActionText={userRoles.length > 1 ? 'Change Role' : 'Logout'}
				secondaryActionText='Sign Out'
				primaryAction={changeRole}
				secondaryAction={() => authData.logout()}
			/>
		);
	}

	return (
		<UserProvider value={userValues}>
			<TenantApp />
		</UserProvider>
	);
};

export default NamespaceApp;
